import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, Snackbar } from "@mui/material";
import { AlertProps } from "@mui/material/Alert";
import { Close, Edit, Save } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { useAppSelector } from "../../app/hooks";
import {
  DELETE_ADMIN_TAG,
  GET_TAG_COUNTS,
  RENAME_ADMIN_TAG,
  tokenOptions,
} from "../../backend/graphql";
import { gridActionSx, gridDeleteSx } from "../widgets/gridActionSx";
import SectionPaper from "../widgets/SectionPaper";
import PageTitle from "../widgets/PageTitle";

type TagCount = { id: string; tag: string; kind: string; count: number };

const ADMIN_TAG = "Admin tag";

// Every tag the stones carry, with how many carry it. The sync tags are shown only, as the next
// sync writes them back; an admin tag can be renamed or deleted, which changes every stone
// carrying it.
const TagSettings = () => {
  const token = useAppSelector((state) => state.auth.token);
  const confirm = useConfirm();
  const [rowModes, setRowModes] = useState<GridRowModesModel>({});
  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    "children" | "severity"
  > | null>(null);

  const { data, loading, refetch } = useQuery(GET_TAG_COUNTS, {
    ...tokenOptions(token),
    fetchPolicy: "network-only",
  });
  const [renameAdminTag] = useMutation(RENAME_ADMIN_TAG, tokenOptions(token));
  const [deleteAdminTag] = useMutation(DELETE_ADMIN_TAG, tokenOptions(token));

  const rows: TagCount[] = (data?.getTagCounts ?? []).map((item: any) => ({
    ...item,
    id: item.kind + ":" + item.tag,
  }));

  const setMode = (id: string, mode: GridRowModes, ignore = false) =>
    setRowModes((previous) => ({
      ...previous,
      [id]: { mode, ignoreModifications: ignore },
    }));

  // Saving the row renames the tag everywhere; the counts are read again, since a tag renamed onto
  // another merges with it.
  const processRowUpdate = async (newRow: TagCount, oldRow: TagCount) => {
    if (newRow.tag.trim().toUpperCase() === oldRow.tag) return oldRow;
    try {
      const result = await renameAdminTag({
        variables: { tag: oldRow.tag, newTag: newRow.tag },
      });
      if (!result.data?.renameAdminTag.success) throw new Error();
      setSnackbar({ children: "Tag renamed.", severity: "success" });
      refetch();
      return { ...newRow, tag: newRow.tag.trim().toUpperCase() };
    } catch {
      setSnackbar({ children: "Unable to rename.", severity: "error" });
      return oldRow;
    }
  };

  const handleDelete = async (row: TagCount) => {
    try {
      await confirm({
        description: `Remove ${row.tag} from the ${row.count} stones carrying it?`,
        confirmationText: "Delete",
      });
    } catch {
      return;
    }
    try {
      await deleteAdminTag({ variables: { tag: row.tag } });
      setSnackbar({ children: "Tag deleted.", severity: "success" });
      refetch();
    } catch {
      setSnackbar({ children: "Unable to delete.", severity: "error" });
    }
  };

  const getActions = ({ id, row }: GridRowParams<TagCount>) => {
    if (row.kind !== ADMIN_TAG) return [];
    if (rowModes[id]?.mode === GridRowModes.Edit)
      return [
        <GridActionsCellItem
          icon={<Close />}
          label="Cancel"
          onClick={() => setMode(row.id, GridRowModes.View, true)}
        />,
        <GridActionsCellItem
          icon={<Save />}
          label="Save"
          sx={gridActionSx}
          onClick={() => setMode(row.id, GridRowModes.View)}
        />,
      ];
    return [
      <GridActionsCellItem
        icon={<Edit />}
        label="Rename"
        sx={gridActionSx}
        onClick={() => setMode(row.id, GridRowModes.Edit)}
      />,
      <GridActionsCellItem
        icon={<DeleteOutlineIcon />}
        label="Delete"
        sx={gridDeleteSx}
        onClick={() => handleDelete(row)}
      />,
    ];
  };

  const columns: GridColDef<TagCount>[] = [
    { field: "tag", headerName: "Tag", flex: 2, editable: true },
    { field: "kind", headerName: "Kind", flex: 1 },
    { field: "count", headerName: "Stones", type: "number", flex: 1 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions,
    },
  ];

  return (
    <SectionPaper sx={{ width: "100%" }}>
      <PageTitle />
      <DataGridPremium
        autoHeight
        loading={loading}
        columns={columns}
        rows={rows}
        initialState={{
          sorting: {
            sortModel: [
              { field: "kind", sort: "asc" },
              { field: "tag", sort: "asc" },
            ],
          },
        }}
        editMode="row"
        rowModesModel={rowModes}
        onRowModesModelChange={setRowModes}
        isCellEditable={(params) => params.row.kind === ADMIN_TAG}
        processRowUpdate={processRowUpdate}
      />
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
          {snackbar?.children}
        </Alert>
      </Snackbar>
    </SectionPaper>
  );
};

export default TagSettings;
